<template>
  <div class="workflow-steps">
    <!-- Empty state -->
    <div v-if="displaySteps.length === 0" class="text-center pa-4 grey--text">
      <v-icon large color="grey lighten-1">mdi-information-outline</v-icon>
      <div class="mt-2">{{ $t('job-manager.no-executed-steps') }}</div>
    </div>

    <!-- Timeline with mixed display: chips for special places, cards for real steps -->
    <v-timeline v-else dense>
      <v-timeline-item
        v-for="(step, index) in displaySteps"
        :key="step.executionId || step.name || index"
        :color="isSpecialPlace(step) ? getPlaceChipColor(step) : 'blue-grey'"
        :small="!isSpecialPlace(step)"
        :icon="isSpecialPlace(step) ? getPlaceIcon(step) : undefined"
      >
        <template v-slot:opposite>
          <span class="step-timestamp" v-if="!isSpecialPlace(step)">{{ step.finishedAt || step.startedAt }}</span>
        </template>

        <!-- Special places (start, completed) as simple chips -->
        <div v-if="isSpecialPlace(step)" class="special-place-item">
          <v-chip
            :color="getPlaceChipColor(step)"
            :outlined="!step.status"
            small
          >
            <v-icon left x-small>{{ getPlaceIcon(step) }}</v-icon>
            {{ formatPlaceName(step.name) }}
            <span v-if="step.finishedAt" class="ml-2 text-caption">{{ formatTime(step.finishedAt) }}</span>
          </v-chip>
        </div>

        <!-- Real executed steps as full cards -->
        <v-card v-else class="elevation-2" @click="showStepDetails(step)">
          <v-card-text>
            <div class="d-flex align-center mb-2 font-weight-medium">
              <span>{{ step.name || `${$t('job-manager.step')} ${index + 1}` }}</span>
              <v-chip
                v-if="step.status"
                small
                :color="getStatusColor((step.status || '').toString().toLowerCase())"
                text-color="white"
                class="ml-2"
              >
                {{ (step.status || '').toString().toLowerCase() }}
              </v-chip>
              <v-chip
                v-if="step.hadFailures"
                small
                color="red"
                outlined
                class="ml-2"
                text-color="red darken-2"
              >
                {{ $t('job-manager.with-retry') }}
              </v-chip>
            </div>
            <div class="text-caption mb-2" v-if="step.prevPlace">
              <strong>{{ $t('job-manager.transition') }}:</strong>
              <span>{{ step.prevPlace }} ➜ {{ step.name || step.place || '-' }}</span>
            </div>
            <div class="text-caption mb-2">
              <strong>{{ $t('job-manager.execution-id') }}:</strong>
              <span>{{ step.executionId || step.id || '-' }}</span>
              <span v-if="step.fromExecutionId" class="ml-4"><strong>{{ $t('job-manager.from-execution-id') }}:</strong> {{ step.fromExecutionId }}</span>
            </div>
            <div class="text-body-2 mb-2">
              <strong>{{ $t('job-manager.started-at') }}:</strong> {{ step.startedAt || '-' }}
              <span class="ml-4"><strong>{{ $t('job-manager.finished-at') }}:</strong> {{ step.finishedAt || '-' }}</span>
            </div>
            <div class="text-body-2 mb-2">
              <strong>{{ $t('job-manager.duration') }}:</strong> {{ step.duration || '-' }}
              <span class="ml-4"><strong>{{ $t('job-manager.attempts') }}:</strong> {{ step.attempts || 1 }}</span>
            </div>
            <div class="text-body-2 mb-2" v-if="hasTransports(step)">
              <strong>{{ $t('job-manager.transports') }}:</strong>
              <v-chip
                v-for="(t, tIdx) in step.transports"
                :key="`st-${index}-t-${tIdx}`"
                x-small
                color="blue lighten-2"
                text-color="white"
                class="mr-1"
              >
                {{ t }}
              </v-chip>
            </div>
            <div class="text-body-2 mb-1" v-if="step.jobClassName">
              <strong>{{ $t('job-manager.job-class-name') }}:</strong> {{ step.jobClassName }}
            </div>
          </v-card-text>
        </v-card>
      </v-timeline-item>
    </v-timeline>
  </div>
</template>

<script>
export default {
  name: 'WorkflowSteps',
  props: {
    steps: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    // Special places that should be shown as simple chips (not full cards)
    specialPlaceNames() {
      return ['start', 'completed', 'idle', 'end', 'finish', 'done'];
    },

    // Filter and return steps to display (in order)
    // - Special places (start, completed) shown as chips
    // - Real executed steps shown as cards
    // - Non-executed steps are hidden
    displaySteps() {
      return this.steps.filter(step => {
        const name = (step.name || step.place || '').toLowerCase();

        // Always show special places (start, completed) as chips
        if (this.specialPlaceNames.includes(name)) {
          return true;
        }

        // For regular steps, only show if executed
        if (step.notExecuted) {
          return false;
        }

        // Must have an execution ID to be considered executed
        if (!step.executionId && !step.id) {
          return false;
        }

        // Skip if ID looks like a placeholder (e.g., "pending-xxx")
        const id = step.executionId || step.id || '';
        if (id.startsWith('pending-')) {
          return false;
        }

        return true;
      });
    }
  },
  methods: {
    isSpecialPlace(step) {
      const name = (step.name || step.place || '').toLowerCase();
      return this.specialPlaceNames.includes(name);
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        running: 'blue',
        success: 'green',
        failed: 'red',
        cancelled: 'grey',
        awaiting_approval: 'purple'
      };
      const key = (status || '').toString().toLowerCase();
      return colors[key] || 'grey';
    },

    getPlaceChipColor(step) {
      const name = (step.name || '').toLowerCase();
      if (name === 'start') return 'blue-grey';
      if (name === 'completed' || name === 'done' || name === 'finish') {
        return step.status === 'success' ? 'success' : 'grey';
      }
      return 'grey';
    },

    getPlaceIcon(step) {
      const name = (step.name || '').toLowerCase();
      if (name === 'start') return 'mdi-play-circle-outline';
      if (name === 'completed' || name === 'done' || name === 'finish') return 'mdi-check-circle-outline';
      if (name === 'end') return 'mdi-stop-circle-outline';
      return 'mdi-circle-outline';
    },

    formatPlaceName(name) {
      if (!name) return '-';
      // Capitalize first letter
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    },

    formatTime(dateStr) {
      if (!dateStr) return '';
      try {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      } catch {
        return '';
      }
    },

    hasTransports(step) {
      return Array.isArray(step.transports) && step.transports.length > 0;
    },

    showStepDetails(step) {
      this.$emit('show-step-details', step);
    }
  }
};
</script>

<style lang="scss" scoped>
.workflow-steps {
  width: 100%;
  padding: 20px 0;
}

.special-place-item {
  padding: 4px 0;
}

.step-timestamp {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}
</style>